import { useEffect, useRef, useState } from "react";
import { httpClient, HttpApiError } from "@client/api/http";
import { PlayerGameSocket, type ConnectionStatus } from "@client/realtime/game-socket";
import {
  captureRecoveryCredential,
  clearInvalidRecoveryCredential,
} from "@client/realtime/recovery";
import { getParticipantToken } from "@client/realtime/token-store";
import type { GameCommand, PlayerBootstrap, PlayerProjection } from "@saboteur/contracts";

export function usePlayerSession(normalizedCode: string, onEnterRoom: (joinCode: string) => void) {
  const [projection, setProjection] = useState<PlayerProjection>();
  const [displayCode, setDisplayCode] = useState("");
  const [roomJoinCode, setRoomJoinCode] = useState("");
  const [gameSocket, setGameSocket] = useState<PlayerGameSocket>();
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("connecting");
  const [credentialRevision, setCredentialRevision] = useState(0);
  useEffect(() => {
    if (normalizedCode === "NEW") {
      setProjection(undefined);
      setGameSocket(undefined);
      setError(undefined);
      return;
    }
    const token = captureRecoveryCredential(normalizedCode);
    if (!token) return;
    setProjection(undefined);
    setError(undefined);
    let active = true;
    const socket = new PlayerGameSocket({ roomCode: normalizedCode, participantToken: token });
    const unsubscribe = socket.subscribe(({ state }) => {
      if (active) setProjection(state);
    });
    const unsubscribeStatus = socket.subscribeStatus(setConnectionStatus);
    setGameSocket(socket);
    socket.connect();
    void (async () => {
      for (let attempt = 0; active && attempt < 6; attempt += 1) {
        try {
          const { state } = await httpClient.getPlayerState(normalizedCode, token);
          if (active) {
            socket.acceptBootstrap(state);
            setError(undefined);
          }
          return;
        } catch (cause) {
          if (!active) return;
          if (
            cause instanceof HttpApiError &&
            ["UNAUTHORIZED", "ROOM_EXPIRED", "ROOM_NOT_FOUND"].includes(cause.detail.code)
          ) {
            clearInvalidRecoveryCredential(normalizedCode, cause.detail.code);
            setConnectionStatus(cause.detail.code === "ROOM_EXPIRED" ? "expired" : "invalid");
            setError(messageFor(cause));
            return;
          }
          if (attempt === 5) {
            setError(messageFor(cause));
            return;
          }
          setConnectionStatus("offline");
          await new Promise((resolve) => window.setTimeout(resolve, 250 * 2 ** attempt));
        }
      }
    })();
    void socket.sync();
    return () => {
      active = false;
      unsubscribe();
      unsubscribeStatus();
      socket.disconnect();
    };
  }, [normalizedCode, credentialRevision]);

  const acceptBootstrap = (bootstrap: PlayerBootstrap): void => {
    // Render the server-confirmed seat and names immediately; lobby controls stay
    // disabled until the route effect installs the one live command socket.
    setProjection(bootstrap.state);
    setGameSocket(undefined);
    setDisplayCode(bootstrap.displayCode);
    setRoomJoinCode(bootstrap.joinCode);
    // A guest is already on `/room/:joinCode`, so navigation alone does not
    // retrigger the route effect after the newly issued token is stored.
    setCredentialRevision((revision) => revision + 1);
    // The route-code effect owns the one live socket after navigation. Creating
    // one here too would register duplicate presence and command listeners.
    onEnterRoom(bootstrap.joinCode);
  };

  const runEntry = async (operation: () => Promise<PlayerBootstrap>): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      acceptBootstrap(await operation());
    } catch (cause) {
      setError(messageFor(cause));
    } finally {
      setBusy(false);
    }
  };

  const sendCommand = async (command: GameCommand): Promise<boolean> => {
    if (submitting.current) return false;
    if (!gameSocket) {
      setError("Still connecting to the room. Try again in a moment.");
      return false;
    }
    submitting.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const ack = await gameSocket.command(command);
      if (ack.status === "rejected" || ack.status === "conflict") {
        setError(ack.message);
        return false;
      }
      return true;
    } catch {
      setError("Could not send that action. Check your connection and try again.");
      return false;
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return {
    projection,
    displayCode,
    currentJoinCode: roomJoinCode || normalizedCode,
    connected: Boolean(gameSocket),
    busy,
    error,
    connectionStatus,
    sendCommand,
    hasCredential: normalizedCode !== "NEW" && Boolean(getParticipantToken(normalizedCode)),
    createRoom: (name: string, avatarIndex?: number) =>
      runEntry(() => httpClient.createRoom(name, avatarIndex)),
    joinRoom: (name: string, avatarIndex?: number) =>
      runEntry(() => httpClient.joinRoom(normalizedCode, name, avatarIndex)),
  };
}

function messageFor(cause: unknown): string {
  return cause instanceof HttpApiError
    ? cause.detail.message
    : cause instanceof Error
      ? cause.message
      : "Could not reach the room.";
}
